<template>
  <wui-modal
    :title="$t('common.joinToChat')"
    :size="40"
    :closable="true"
  >
    <div class="flex items-center gap-3 mb-3">
      <contact-avatar
        :image="roomInfo.avatar ? getFullURL(roomInfo.avatar) : null"
        :name="roomInfo.name"
        :is-room="true"
      />
      <div class="grid">
        <span
          class="text-heading-m-16-22 truncate text-fg-base-fg3"
          :title="roomInfo.name"
        >{{ roomInfo.name }}</span>
        <span
          class="text-label-r-13-18 text-fg-base-fg22 truncate"
          :title="countMemberTextView"
        >{{ countMemberTextView }}</span>
      </div>
    </div>
    <p class="text-body-r-14-20 text-fg-base-fg10 mb-5">
      {{ $t('common.joinToChatDescription') }}
    </p>

    <template #footer>
      <wui-btn
        :size="40"
        data-test="cancel"
        variant="neut"
        state="alpha"
        @click="closeDialog()"
      >
        {{ $t('common.no') }}
      </wui-btn>
      <wui-btn
        :size="40"
        data-test="submit"
        variant="brand"
        state="alpha"
        auto-loading
        @click="onSubmit()"
      >
        <span>{{ $t('common.yes') }}</span>
      </wui-btn>
    </template>
  </wui-modal>
</template>

<script setup lang="ts">
import { WuiBtn, WuiModal, closeDialogKey, safeInject, useRenderNotifier } from '@wui/common-library'
import { BriefStatusResponse } from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { ContactAvatar } from '@/entities/contact-avatar'
import { useStoreRooms } from '@/entities/room'
import { useRoom } from '@/entities/use-room'


import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { getTextForDifferentCount } from '@/shared/utils/get-text-for-different-count'
import { optionalPush } from '@/shared/utils/router'
import { getChatRoute, getFullURL } from '@/shared/utils/url'

const props = defineProps<{
  pServed: string,
  roomInfo: BriefStatusResponse
  skipChatNavigation?: boolean
}>()

const { t, tc } = useLocalization()
const { addMember } = useRoom()
const { currentUser } = useCurrentUser()
const { roomsList } = storeToRefs(useStoreRooms())
const { notifyError } = useRenderNotifier()

const closeModal = safeInject(closeDialogKey)

const closeDialog = () => {
  closeModal(undefined)
}

const wordsForCountMemberText = {
  forOne: tc('common.participant', 0),
  forTwo: tc('common.participant', 1),
  forFive: tc('common.participant', 2),
}

const countMemberTextView = computed(() => getTextForDifferentCount(props.roomInfo.count, wordsForCountMemberText))
const onSubmit = async () => {
  if (!currentUser.value) return

  const res = await addMember(props.pServed, currentUser.value?.internalNumber, 'AddMember')

  if (!res) {
    notifyError(t('common.failedToJoinToChat'))
    return
  }

  const room = roomsList.value.find((room) => room.pServedUser === props.pServed)
  if (room && !props.skipChatNavigation) {
    await optionalPush(getChatRoute(room))
  }
  closeModal(true)
}
</script>
